/**
 * Форматирование чисел и дат страниц аналитики продаж.
 *
 * Правила (SPEC_2026-10-20_ANALYTICS_WAVE2_SALES.md, раздел 4): «1 234 шт», «450 ₽», проценты с одним
 * знаком, `null` и пустое значение — «—», а не «0» (пустое не равно нулю).
 */

import { SESSION_EXPIRED_MESSAGE } from '../../../components/ui/sessionExpired'

const NBSP = ' '
const MINUS = '−'
const DASH = '—'

const numberFormats = new Map<number, Intl.NumberFormat>()

function numberFormat(maxFraction: number): Intl.NumberFormat {
  let format = numberFormats.get(maxFraction)
  if (!format) {
    format = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: maxFraction })
    numberFormats.set(maxFraction, format)
  }
  return format
}

const percentFormat = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

/** Значение — конечное число (не null, не NaN). */
export function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/** Число из ответа API: числовые строки MySQL приводим к числу, пустое остаётся `null`. */
export function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const result = Number(value)
  return Number.isFinite(result) ? result : null
}

/** «1 234 шт». */
export function formatQty(value: number | null | undefined): string {
  if (!isNumber(value)) return DASH
  return `${numberFormat(0).format(value)}${NBSP}шт`
}

/** Просто число с разделителями разрядов («1 234»), без единицы. */
export function formatCount(value: number | null | undefined): string {
  if (!isNumber(value)) return DASH
  return numberFormat(0).format(value)
}

/** «450 ₽». По умолчанию без копеек: суммы продаж считаются целыми рублями. */
export function formatRub(value: number | null | undefined, maxFraction = 0): string {
  if (!isNumber(value)) return DASH
  return `${numberFormat(maxFraction).format(value)}${NBSP}₽`
}

/**
 * Доля от 0 до 1 → «87,3%». Проценты в контракте API — доли (`buyoutsQty / (buyoutsQty + returnsQty)`),
 * поэтому умножаем на 100 здесь, в одном месте.
 */
export function formatPercent(fraction: number | null | undefined): string {
  if (!isNumber(fraction)) return DASH
  return `${percentFormat.format(fraction * 100)}%`
}

/** Знаковое число с настоящим минусом: «+12,4», «−3,1»; ноль без знака. */
function signed(value: number, formatted: string): string {
  if (value > 0) return `+${formatted}`
  if (value < 0) return `${MINUS}${formatted}`
  return formatted
}

/**
 * Относительное изменение «к сравнению»: (текущее − прежнее) / |прежнее|.
 * `null`, если сравнивать нечего: нет одного из значений или прежнее равно нулю.
 */
export function relativeChange(current: number | null | undefined, previous: number | null | undefined): number | null {
  if (!isNumber(current) || !isNumber(previous) || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

/** «+12,4%» / «−3,1%» / «—». Вход — дробь (0,124). */
export function formatDeltaPercent(change: number | null | undefined): string {
  if (!isNumber(change)) return DASH
  const text = percentFormat.format(Math.abs(change) * 100)
  return signed(Math.abs(change) < 0.0005 ? 0 : change, `${text}%`)
}

/** Разница долей в процентных пунктах: «+1,2 п.п.». Вход — доли (0,873 и 0,861). */
export function formatDeltaPoints(current: number | null | undefined, previous: number | null | undefined): string {
  if (!isNumber(current) || !isNumber(previous)) return DASH
  const diff = (current - previous) * 100
  const text = percentFormat.format(Math.abs(diff))
  return signed(Math.abs(diff) < 0.05 ? 0 : diff, `${text}${NBSP}п.п.`)
}

/** Направление изменения для плитки: ноль и «нечего сравнивать» — flat. */
export function trendOf(change: number | null | undefined): 'up' | 'down' | 'flat' {
  if (!isNumber(change) || Math.abs(change) < 0.0005) return 'flat'
  return change > 0 ? 'up' : 'down'
}

/** Короткая запись для ячеек матрицы и оси графика: 1 234 → «1,2 тыс.», 2 500 000 → «2,5 млн». */
export function formatCompact(value: number | null | undefined): string {
  if (!isNumber(value)) return DASH
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${numberFormat(1).format(value / 1_000_000)}${NBSP}млн`
  if (abs >= 10_000) return `${numberFormat(0).format(value / 1_000)}${NBSP}тыс.`
  if (abs >= 1_000) return `${numberFormat(1).format(value / 1_000)}${NBSP}тыс.`
  return numberFormat(0).format(value)
}

// ── Даты ────────────────────────────────────────────────────────────────────

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/** `YYYY-MM-DD` → `DD.MM.YYYY`; строку со временем режем до даты; пусто — «—». */
export function formatDate(value: string | null | undefined): string {
  if (!value) return DASH
  const match = ISO_DATE.exec(String(value).slice(0, 10))
  if (!match) return String(value)
  return `${match[3]}.${match[2]}.${match[1]}`
}

/** `YYYY-MM-DD` → `DD.MM`: подпись оси графика по дням. */
export function formatDayMonth(value: string | null | undefined): string {
  if (!value) return DASH
  const match = ISO_DATE.exec(String(value).slice(0, 10))
  return match ? `${match[3]}.${match[2]}` : String(value)
}

/** Дата и время — `DD.MM.YYYY HH:MM` в часовом поясе браузера. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return DASH
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short' }).format(date)
}

/** `YYYY-MM-DD` по локальному календарю (не UTC — иначе ночью сутки «съезжают»). */
export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export function todayIso(now: Date = new Date()): string {
  return toIsoDate(now)
}

/** Сдвиг даты `YYYY-MM-DD` на `days` суток (в полдень — чтобы переход на летнее время не сдвигал сутки). */
export function addDays(iso: string, days: number): string {
  const match = ISO_DATE.exec(iso)
  if (!match) return iso
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days, 12, 0, 0)
  return toIsoDate(date)
}

/** Строка — настоящая календарная дата вида `YYYY-MM-DD` (не 2026-02-31). */
export function isIsoDate(value: string): boolean {
  const match = ISO_DATE.exec(value)
  if (!match) return false
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const date = new Date(Date.UTC(year, month - 1, day))
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

/** `2026-W30` → «нед. 30»; всё остальное возвращаем как есть. */
export function formatWeekLabel(value: string): string {
  const match = /^(\d{4})-W(\d{1,2})$/.exec(value)
  return match ? `нед.${NBSP}${Number(match[2])}` : value
}

/**
 * Подпись точки ряда по дням или неделям (волна 6: ключ недели — дата её понедельника). Короткая — для оси
 * («12.10», «нед. 13.10»), полная — для подсказки и заголовков («12.10.2026», «неделя с 13.10.2026»).
 * Ключ вида `2026-W41` тоже понимается.
 */
export function formatSeriesDate(value: string, granularity: 'day' | 'week', full = false): string {
  if (/^\d{4}-W\d{1,2}$/.test(value)) return formatWeekLabel(value)
  if (granularity === 'week') return full ? `неделя с ${formatDate(value)}` : `нед.${NBSP}${formatDayMonth(value)}`
  return full ? formatDate(value) : formatDayMonth(value)
}

/** Ответ сервиса аналитики не пришёл или пришёл ошибкой сервера (5xx, сеть, таймаут). */
export const SERVICE_DOWN_MESSAGE = 'Сервис аналитики не ответил — повторите через минуту'

const hasCyrillic = (text: string) => /[А-Яа-яЁё]/.test(text)

/**
 * Текст ошибки API для пользователя (Ф12 аудита 20.10): 401 — «сессия истекла» (рядом карточка и уведомление
 * покажут кнопку «Войти» в новой вкладке), 502–504, сеть и таймаут — «сервис не ответил», остальное — русское
 * сообщение гейтвея или запасной текст. Сырые английские сообщения («Request failed with status code 500»,
 * «Network Error») на экран не попадают.
 */
export function analyticsErrorMessage(error: unknown, fallback: string): string {
  const source = (error ?? {}) as any
  const status = Number(source?.response?.status)
  if (status === 401) return SESSION_EXPIRED_MESSAGE
  const message = source?.response?.data?.error?.message ?? source?.response?.data?.message ?? source?.message
  const text = message === undefined || message === null ? '' : String(message).trim()
  const readable = text !== '' && hasCyrillic(text)
  if (status >= 502 && status <= 504) return SERVICE_DOWN_MESSAGE
  if (status >= 500) return readable ? text : SERVICE_DOWN_MESSAGE
  // Нет ответа сервера: сеть оборвалась или вышел таймаут (axios кладёт запрос, но не ответ).
  if (!source?.response && (source?.request || source?.code === 'ECONNABORTED' || source?.code === 'ERR_NETWORK')) {
    return readable ? text : SERVICE_DOWN_MESSAGE
  }
  return readable ? text : fallback
}
